import { Button, ExternalLink, Input, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { formatMoney, getCurrencySymbol, priceStringAsNumber } from '../../../utils/format-money';
import { DOCS_URLS, I18N_COMPONENTS } from '../constants';
import type { VariantPriceInput } from '../types';

const UOM_PATTERN = /^[a-zA-Z0-9-]+$/;

type PriceRowProps = {
  price: VariantPriceInput;
  index: number;
  currencyCode: string;
  canEdit: boolean;
  isNew: boolean;
  isUnitOfMeasureRequired: boolean;
  isUnitOfMeasureDisabled: boolean;
  onSave: (price: VariantPriceInput) => void;
  onDelete: () => void;
};

type RowFormData = {
  quantity: string;
  price: string;
  unit_of_measure: string;
};

type RowErrors = {
  quantity?: string;
  price?: string;
  unit_of_measure?: string;
};

function formDataFromPrice(price: VariantPriceInput): RowFormData {
  return {
    quantity: String(price.quantity),
    price: String(price.price),
    unit_of_measure: price.unit_of_measure ?? '',
  };
}

export function PriceRow({
  price,
  index,
  currencyCode,
  canEdit,
  isNew,
  isUnitOfMeasureRequired,
  isUnitOfMeasureDisabled,
  onSave,
  onDelete,
}: PriceRowProps) {
  const [editing, setEditing] = useState(isNew);
  const [formData, setFormData] = useState<RowFormData>(() => formDataFromPrice(price));
  const [errors, setErrors] = useState<RowErrors>({});

  const startEditing = () => {
    setFormData(formDataFromPrice(price));
    setErrors({});
    setEditing(true);
  };

  const save = () => {
    const nextErrors: RowErrors = {};
    const quantityNum = Number(formData.quantity);
    if (!formData.quantity.trim() || !Number.isInteger(quantityNum) || quantityNum < 1) {
      nextErrors.quantity = t(`${I18N_COMPONENTS}.price_row.errors.quantity`);
    }

    let priceNum: number | null = null;
    if (!formData.price.trim()) {
      nextErrors.price = t(`${I18N_COMPONENTS}.price_row.errors.price`);
    } else {
      try {
        priceNum = priceStringAsNumber(formData.price);
      } catch {
        nextErrors.price = t(`${I18N_COMPONENTS}.price_row.errors.price`);
      }
    }

    const uom = formData.unit_of_measure.trim();
    if (isUnitOfMeasureRequired) {
      if (!uom) {
        nextErrors.unit_of_measure = t(`${I18N_COMPONENTS}.price_row.errors.unit_of_measure`);
      } else if (!UOM_PATTERN.test(uom)) {
        nextErrors.unit_of_measure = t(
          `${I18N_COMPONENTS}.price_row.errors.unit_of_measure_pattern`,
        );
      }
    } else if (uom && !UOM_PATTERN.test(uom)) {
      nextErrors.unit_of_measure = t(`${I18N_COMPONENTS}.price_row.errors.unit_of_measure_pattern`);
    }

    setErrors(nextErrors);
    if (priceNum === null || Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave({
      quantity: quantityNum,
      price: priceNum,
      tax_type: 'net',
      unit_of_measure: uom || undefined,
    });
    setEditing(false);
  };

  if (!editing) {
    return (
      <Table.Row id={`price-${price.quantity}-${index}`} index={index}>
        <Table.Cell>
          <Text>
            {price.unit_of_measure
              ? `${price.quantity} (${price.unit_of_measure})`
              : `${price.quantity}+`}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text>{formatMoney(price.price, currencyCode)}</Text>
        </Table.Cell>
        <Table.Cell>
          {canEdit && (
            <span className="flex items-center gap-3">
              <button
                type="button"
                onClick={startEditing}
                className="font-medium text-spark-purple hover:brightness-125"
              >
                {t(`${I18N_COMPONENTS}.price_row.edit`)}
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="font-medium text-red-600 hover:text-red-900"
              >
                {t(`${I18N_COMPONENTS}.price_row.remove`)}
              </button>
            </span>
          )}
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <Table.Row id={`edit-${price.quantity}-${index}`} index={index}>
      <Table.Cell>
        <span className="flex flex-wrap items-end gap-3">
          <Input
            label={t(`${I18N_COMPONENTS}.price_row.quantity`)}
            value={formData.quantity}
            onChange={(value) => setFormData((f) => ({ ...f, quantity: value }))}
            inputMode="numeric"
            short
            error={errors.quantity}
          />
          <Input
            label={t(`${I18N_COMPONENTS}.price_row.unit_of_measure`)}
            value={formData.unit_of_measure}
            onChange={(value) => setFormData((f) => ({ ...f, unit_of_measure: value }))}
            placeholder={t(`${I18N_COMPONENTS}.price_row.unit_of_measure_placeholder`)}
            pattern="[a-zA-Z0-9\-]+"
            disabled={isUnitOfMeasureDisabled}
            error={errors.unit_of_measure}
          />
          <ExternalLink link={DOCS_URLS.quantityPricing} showIcon>
            {t('links:learn_more')}
          </ExternalLink>
        </span>
      </Table.Cell>
      <Table.Cell>
        <Input
          label={t(`${I18N_COMPONENTS}.price_row.price`)}
          value={formData.price}
          onChange={(value) => setFormData((f) => ({ ...f, price: value }))}
          prefix={getCurrencySymbol(currencyCode)}
          inputMode="decimal"
          error={errors.price}
        />
      </Table.Cell>
      <Table.Cell>
        <span className="flex items-center justify-end gap-3">
          <Button variant="primary" size="sm" onClick={save}>
            {t(`${I18N_COMPONENTS}.price_row.save`)}
          </Button>
          <button
            type="button"
            onClick={() => (isNew ? onDelete() : setEditing(false))}
            className="font-medium text-spark-purple hover:brightness-125"
          >
            {t(`${I18N_COMPONENTS}.price_row.cancel`)}
          </button>
        </span>
      </Table.Cell>
    </Table.Row>
  );
}
